import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/common/CustomDialog';
import { Button } from '@/components/ui/button';
import { ExternalLink, ChevronLeft, ChevronRight, Loader2, Plus, Image, ChevronDown } from 'lucide-react';
import { fetchDiffsPages } from '@/utils/diffsAnalyticsAPI';
import type { DiffsPagesResponse, DiffsPagesItem, DiffsBreakdownType, DiffsCountMode } from './types';
import { shiftQuarterBack } from './types';
import type { ExampleConfig, ExampleTableRow } from '@/components/reports/example/types';
import { TableProperties } from 'lucide-react';

// Table info for picker
export interface TableOption {
  id: string;
  title: string;
  rowCount: number;
}

interface DiffsDrilldownModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  period: string;
  breakdownType: DiffsBreakdownType;
  breakdownValue: string;
  shiftQuarters?: boolean;
  allPeriods?: string[];
  requireAllPeriods?: boolean;
  countMode?: DiffsCountMode;
  onCreateExample?: (config: ExampleConfig) => void;
  onAddToTable?: (row: Omit<ExampleTableRow, 'id'>, tableId?: string) => void;
  availableTables?: TableOption[];
  lastSelectedTableId?: string | null;
  onTableSelected?: (tableId: string | null) => void;
}

const DiffsDrilldownModal: React.FC<DiffsDrilldownModalProps> = ({
  isOpen,
  onOpenChange,
  period,
  breakdownType,
  breakdownValue,
  shiftQuarters = true,
  allPeriods,
  requireAllPeriods,
  countMode = 'unique',
  onCreateExample,
  onAddToTable,
  availableTables = [],
  lastSelectedTableId,
  onTableSelected
}) => {
  const [data, setData] = useState<DiffsPagesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedDiffs, setExpandedDiffs] = useState<Set<string>>(new Set());
  const [tablePickerOpen, setTablePickerOpen] = useState<string | null>(null); // event_id when picker is open
  const tablePickerRef = useRef<HTMLDivElement>(null);

  const displayPeriod = shiftQuarters ? shiftQuarterBack(period) : period;

  // Helper to extract before/after URLs from compare_viewer_url
  const extractUrlsFromCompareViewer = (compareUrl?: string): { beforeUrl?: string; afterUrl?: string } => {
    if (!compareUrl) return {};

    try {
      const url = new URL(compareUrl);
      const url1 = url.searchParams.get('url1');
      const url2 = url.searchParams.get('url2');

      return {
        beforeUrl: url1 ? decodeURIComponent(url1) : undefined,
        afterUrl: url2 ? decodeURIComponent(url2) : undefined
      };
    } catch (e) {
      console.error('[DiffsDrilldownModal] Failed to parse compare_viewer_url:', e);
      return {};
    }
  };

  // Close table picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tablePickerRef.current && !tablePickerRef.current.contains(e.target as Node)) {
        setTablePickerOpen(null);
      }
    };
    if (tablePickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [tablePickerOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchDiffsPages({
          period,
          breakdown_type: breakdownType,
          breakdown_value: breakdownValue,
          pagination: { page: currentPage, per_page: 20 },
          count_mode: countMode,
          ...(requireAllPeriods && allPeriods ? {
            all_periods: allPeriods,
            require_all_periods: true
          } : {})
        });
        setData(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, period, breakdownType, breakdownValue, currentPage, allPeriods, requireAllPeriods, countMode]);

  const toggleExpanded = (diffId: string) => {
    setExpandedDiffs(prev => {
      const next = new Set(prev);
      if (next.has(diffId)) {
        next.delete(diffId);
      } else {
        next.add(diffId);
      }
      return next;
    });
  };

  const formatEventType = (eventType: string) => {
    return eventType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const renderDiffItem = (diff: DiffsPagesItem) => {
    const isExpanded = expandedDiffs.has(diff.diff_id);

    return (
      <div key={diff.diff_id} className="border rounded-lg p-4 bg-white">
        <div className="flex items-start gap-3">
          {diff.logo_url && (
            <img
              src={diff.logo_url}
              alt={diff.company_name}
              className="w-10 h-10 rounded object-cover flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <a
                href={`/company/${diff.company_slug}`}
                className="font-medium text-blue-600 hover:underline"
              >
                {diff.company_name}
              </a>
              {diff.category && (
                <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{diff.category}</span>
              )}
              {diff.employees && (
                <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{diff.employees}</span>
              )}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {diff.page_name}
              {diff.page_url && (
                <a
                  href={diff.page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 text-blue-500 hover:underline inline-flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm font-medium">
                {diff.event_count} event{diff.event_count !== 1 ? 's' : ''}
              </span>
              <button
                onClick={() => toggleExpanded(diff.diff_id)}
                className="text-xs text-blue-600 hover:underline"
              >
                {isExpanded ? 'Hide details' : 'Show details'}
              </button>
              <a
                href={diff.combined_annotation?.compare_viewer_url || `/diffs/${diff.diff_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline ml-auto"
              >
                View diff →
              </a>
            </div>
          </div>
        </div>

        {isExpanded && diff.events.length > 0 && (
          <div className="mt-3 pt-3 border-t space-y-2">
            {diff.events.map(event => (
              <div key={event.event_id} className="text-sm flex items-start gap-2">
                <div className="flex-1">
                  <span className="font-medium text-gray-700">
                    {formatEventType(event.event_type)}:
                  </span>{' '}
                  <span className="text-gray-600">{event.description}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {event.annotation?.compare_viewer_url && (
                    <a
                      href={event.annotation.compare_viewer_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View
                    </a>
                  )}
                  {/* Create Example - always show when handler provided, disabled without images */}
                  {onCreateExample && (() => {
                    // Determine correct image URLs based on annotation type
                    // Use diff-level URLs as fallbacks when event doesn't have specific images
                    // Extract URLs from compare_viewer_url as last resort

                    // Try to get URLs from combined_annotation first, or extract from compare_viewer_url
                    const diffUrls = diff.combined_annotation?.before_viewer_url && diff.combined_annotation?.after_viewer_url
                      ? {
                          beforeUrl: diff.combined_annotation.before_viewer_url,
                          afterUrl: diff.combined_annotation.after_viewer_url
                        }
                      : extractUrlsFromCompareViewer(diff.combined_annotation?.compare_viewer_url || event.annotation?.compare_viewer_url);

                    let beforeImage: string | undefined;
                    let afterImage: string | undefined;

                    if (event.annotation?.type === 'after_only') {
                      // Use unannotated before and annotated after for "added" events
                      beforeImage = event.annotation.before_viewer_url || diffUrls.beforeUrl;
                      afterImage = event.annotation.after_annotated_urls?.[0] || event.annotation.after_viewer_url || diffUrls.afterUrl;
                    } else if (event.annotation?.type === 'before_only') {
                      // Use annotated before and unannotated after for "removed" events
                      beforeImage = event.annotation.before_annotated_urls?.[0] || event.annotation.before_viewer_url || diffUrls.beforeUrl;
                      afterImage = event.annotation.after_viewer_url || diffUrls.afterUrl;
                    } else {
                      // Normal diff - use annotated images for both sides, fall back to diff-level URLs
                      beforeImage = event.annotation?.before_annotated_urls?.[0] || event.annotation?.before_viewer_url || diffUrls.beforeUrl;
                      afterImage = event.annotation?.after_annotated_urls?.[0] || event.annotation?.after_viewer_url || diffUrls.afterUrl;
                    }

                    const hasImage = beforeImage || afterImage;

                    return (
                      <button
                        onClick={() => {
                          if (beforeImage && afterImage) {
                            onCreateExample({
                              caption: event.description,
                              beforeImageUrl: beforeImage,
                              afterImageUrl: afterImage,
                              compareViewerUrl: event.annotation.compare_viewer_url,
                              beforeCropCenter: event.annotation.before_marker,
                              afterCropCenter: event.annotation.after_marker,
                              beforeMarker: event.annotation.before_marker,
                              afterMarker: event.annotation.after_marker,
                              companySlug: diff.company_slug,
                              companyName: diff.company_name,
                              eventType: event.event_type,
                              eventId: event.event_id,
                              period: displayPeriod
                            });
                          }
                        }}
                        disabled={!hasImage}
                        className={`text-xs flex items-center gap-1 ${
                          hasImage
                            ? 'text-purple-600 hover:underline'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                        title={hasImage ? 'Create visual example' : 'No images available'}
                      >
                        <Image className="w-3 h-3" />
                        Example
                      </button>
                    );
                  })()}
                  {/* Add to Table - with picker dropdown */}
                  {onAddToTable && (
                    <div className="relative" ref={tablePickerOpen === event.event_id ? tablePickerRef : undefined}>
                      <div className="flex items-center">
                        {/* Quick add to last selected table */}
                        {lastSelectedTableId && availableTables.find(t => t.id === lastSelectedTableId) && (
                          <button
                            onClick={() => {
                              onAddToTable({
                                period: displayPeriod,
                                companyName: diff.company_name,
                                companySlug: diff.company_slug,
                                eventType: event.event_type,
                                description: event.description,
                                compareViewerUrl: event.annotation?.compare_viewer_url,
                                eventId: event.event_id
                              }, lastSelectedTableId);
                            }}
                            className="text-xs text-teal-600 hover:underline flex items-center gap-1"
                            title={`Add to "${availableTables.find(t => t.id === lastSelectedTableId)?.title}"`}
                          >
                            <TableProperties className="w-3 h-3" />
                            + Table
                          </button>
                        )}
                        <button
                          onClick={() => setTablePickerOpen(tablePickerOpen === event.event_id ? null : event.event_id)}
                          className="text-xs text-teal-600 hover:underline flex items-center gap-0.5 ml-0.5"
                          title="Choose table"
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </div>
                      {/* Table picker dropdown */}
                      {tablePickerOpen === event.event_id && (
                        <div className="absolute right-0 top-full mt-1 bg-white border rounded-md shadow-lg z-50 min-w-[180px] py-1">
                          {availableTables.length > 0 && (
                            <>
                              <div className="px-2 py-1 text-xs font-medium text-gray-500 border-b">Add to existing table</div>
                              {availableTables.map(table => (
                                <button
                                  key={table.id}
                                  onClick={() => {
                                    onAddToTable({
                                      period: displayPeriod,
                                      companyName: diff.company_name,
                                      companySlug: diff.company_slug,
                                      eventType: event.event_type,
                                      description: event.description,
                                      compareViewerUrl: event.annotation?.compare_viewer_url,
                                      eventId: event.event_id
                                    }, table.id);
                                    onTableSelected?.(table.id);
                                    setTablePickerOpen(null);
                                  }}
                                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 flex items-center justify-between"
                                >
                                  <span className="truncate">{table.title}</span>
                                  <span className="text-gray-400 ml-2">({table.rowCount})</span>
                                </button>
                              ))}
                              <div className="border-t my-1" />
                            </>
                          )}
                          <button
                            onClick={() => {
                              onAddToTable({
                                period: displayPeriod,
                                companyName: diff.company_name,
                                companySlug: diff.company_slug,
                                eventType: event.event_type,
                                description: event.description,
                                compareViewerUrl: event.annotation?.compare_viewer_url,
                                eventId: event.event_id
                              }, undefined); // undefined = create new table
                              setTablePickerOpen(null);
                            }}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 text-teal-600 font-medium flex items-center gap-1"
                          >
                            <Plus className="w-3 h-3" />
                            Create new table
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[800px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {formatEventType(breakdownValue)} in {displayPeriod}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-lg">
              {error}
            </div>
          )}

          {!loading && !error && data && (
            <div className="space-y-3">
              {data.diffs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No results found
                </div>
              ) : (
                data.diffs.map(renderDiffItem)
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between items-center mt-4 pt-4 border-t">
          <div className="text-sm text-gray-500">
            {data && (
              <>
                Showing {data.diffs.length} of {data.pagination.total_count} results
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {data && data.pagination.total_pages > 1 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || loading}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {data.pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(data.pagination.total_pages, p + 1))}
                  disabled={currentPage === data.pagination.total_pages || loading}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </>
            )}
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DiffsDrilldownModal;
